import { useMutation, useQueryClient } from '@tanstack/react-query'
import type { UseMutationResult } from '@tanstack/react-query'

import { useApi } from '@/app/api'
import { failureOf } from '@/shared/api'
import { keys } from '@/shared/api/queries'
import type { Task } from '@/shared/api/queries'

type Outcome = 'done' | 'dismissed'

export interface Change {
  id: string
  outcome: Outcome
}

export function useResolveTask(): UseMutationResult<Task, Error, Change, { tasks?: Task[] }> {
  const api = useApi()
  const client = useQueryClient()

  return useMutation({
    mutationFn: async ({ id, outcome }: Change) => {
      const result =
        outcome === 'done'
          ? await api.POST('/tasks/{id}/complete', { params: { path: { id } } })
          : await api.POST('/tasks/{id}/dismiss', { params: { path: { id } }, body: {} })

      if (result.error) {
        throw failureOf(result.error)
      }

      return result.data
    },

    onMutate: async ({ id, outcome }: Change) => {
      await client.cancelQueries({ queryKey: keys.tasks })

      const tasks = client.getQueryData<Task[]>(keys.tasks)

      client.setQueryData<Task[]>(keys.tasks, (current) =>
        current?.map((task) => (task.id === id ? { ...task, status: outcome } : task)),
      )

      return { tasks }
    },

    onError: (_error, _change, context) => {
      if (context?.tasks) {
        client.setQueryData(keys.tasks, context.tasks)
      }
    },

    onSettled: () => {
      void client.invalidateQueries({ queryKey: keys.tasks })
      void client.invalidateQueries({ queryKey: keys.dashboard })
    },
  })
}
